/**
 * 全社PLのドリルダウン集計。
 * 売上 → 事業部 → 階層(拠点→事業所→コース→人)→ 個別取引 まで辿れるようにする。
 *
 * 階層ノードの `path`(祖先IDの配列)を使い、取引の金額を自分と祖先すべてに積み上げる。
 * 階層が未設定の取引は「(階層未設定)」としてまとめ、合計から漏れないようにする。
 */
import type { Account, HierarchyNode, PlLine, Transaction, Yen } from '@core/lib/types';

export type NodeAmount = {
  /** 自分と配下すべての合計 */
  total: Yen;
  /** このノードに直接紐づく分 */
  own: Yen;
};

export type DrilldownResult = {
  /** ノードID → 金額 */
  byNode: Map<string, NodeAmount>;
  /** 階層が未設定の取引の合計 */
  unassigned: Yen;
  /** 対象のPL行に該当する取引だけを絞ったもの */
  transactions: Transaction[];
};

/** 対象のPL行(売上・原価・経費)に該当する取引だけを残す */
export function filterByPlLine(
  transactions: Transaction[],
  accounts: Account[],
  plLine: PlLine,
): Transaction[] {
  const lineOf = new Map(accounts.map((a) => [a.id, a.pl_line]));
  return transactions.filter((t) => (lineOf.get(t.account_id) ?? null) === plLine);
}

export function aggregateByNode(
  transactions: Transaction[],
  nodes: HierarchyNode[],
  accounts: Account[],
  plLine: PlLine,
): DrilldownResult {
  const target = filterByPlLine(transactions, accounts, plLine);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const byNode = new Map<string, NodeAmount>();
  let unassigned = 0;

  const add = (nodeId: string, amount: Yen, own: boolean) => {
    const current = byNode.get(nodeId) ?? { total: 0, own: 0 };
    byNode.set(nodeId, { total: current.total + amount, own: current.own + (own ? amount : 0) });
  };

  for (const t of target) {
    const nodeId = t.hierarchy_node_id;
    const node = nodeId ? byId.get(nodeId) : undefined;
    if (!node) {
      unassigned += t.amount;
      continue;
    }
    add(node.id, t.amount, true);
    for (const ancestorId of node.path) add(ancestorId, t.amount, false);
  }

  return { byNode, unassigned, transactions: target };
}

/** そのノード配下(自分を含む)の取引を返す。根拠の一覧表示に使う */
export function transactionsUnder(
  nodeId: string,
  transactions: Transaction[],
  nodes: HierarchyNode[],
): Transaction[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return transactions.filter((t) => {
    if (!t.hierarchy_node_id) return false;
    if (t.hierarchy_node_id === nodeId) return true;
    const node = byId.get(t.hierarchy_node_id);
    return node ? node.path.includes(nodeId) : false;
  });
}

/** 階層未設定の取引 */
export function transactionsUnassigned(transactions: Transaction[]): Transaction[] {
  return transactions.filter((t) => !t.hierarchy_node_id);
}

export function childrenOf(nodes: HierarchyNode[], parentId: string | null): HierarchyNode[] {
  return nodes.filter((n) => (n.parent_id ?? null) === parentId);
}
